/**
 * Auto-redirects first-time visitors on the bare English root ("/") to the
 * matching localized marketing URL based on `navigator.languages`.
 *
 * Rules:
 *   - Only acts on "/", never on any other path.
 *   - Skips if the user has already chosen a locale (the i18next detector
 *     stores it under "vg.locale" in localStorage).
 *   - Skips if the URL carries `?lang=` (explicit override wins).
 *   - Sets the preference key on redirect so we never bounce twice.
 *   - Replaces (not pushes) so the browser Back button does not loop.
 */

import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  localizedHref,
  type SupportedMarketingLocale,
} from "@/i18n/marketing";

const STORAGE_KEY = "vg.locale";

/** Map a BCP-47 language tag to a marketing locale, or null. */
function tagToLocale(tag: string): SupportedMarketingLocale | null {
  const t = tag.toLowerCase();
  if (t.startsWith("pt")) return "pt-BR";
  if (t.startsWith("fr")) return "fr";
  if (t.startsWith("es")) return "es";
  if (t.startsWith("ja")) return "ja";
  if (t.startsWith("en")) return "en";
  return null;
}

export function LocaleRedirect() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (pathname !== "/") return;
    if (typeof window === "undefined") return;

    try {
      // Explicit override via ?lang=
      const params = new URLSearchParams(search);
      if (params.has("lang")) {
        window.localStorage.setItem(STORAGE_KEY, params.get("lang") || "en");
        return;
      }

      // Already chose a locale earlier — respect it (no bouncing)
      if (window.localStorage.getItem(STORAGE_KEY)) return;

      const langs =
        (navigator.languages && navigator.languages.length
          ? navigator.languages
          : [navigator.language]) ?? [];

      let target: SupportedMarketingLocale | null = null;
      for (const tag of langs) {
        const m = tagToLocale(tag);
        if (m) {
          target = m;
          break;
        }
      }

      if (!target || target === "en") {
        // Pin English so we don't re-evaluate on every visit
        window.localStorage.setItem(STORAGE_KEY, "en");
        return;
      }

      window.localStorage.setItem(STORAGE_KEY, target);
      navigate(localizedHref("/", target), { replace: true });
    } catch {
      // localStorage unavailable (private mode etc.) — fail open, stay on /
    }
  }, [pathname, search, navigate]);

  return null;
}
